import { Fragment, useState } from "react";
import {
  AuditClient,
  PrincipalsClient,
  type AuditEventView,
  type PrincipalSummary,
} from "../api/generated";
import { Alert, Empty, Field, Panel, useAsync, when } from "../components/ui";

const auditClient = new AuditClient("");
const principals = new PrincipalsClient("");

const PAGE_SIZE = 100;

/** The entity types the hub writes audit rows for, for the filter dropdown. */
const ENTITY_TYPES = [
  "principal", "party", "party_node", "audience_group", "party_attribute_definition",
  "stream", "subject_schema", "message_flag_definition", "message", "subscription", "tenant",
];

interface Filters {
  from: string;
  to: string;
  actor: string;
  action: string;
  entityType: string;
  entityId: string;
}

const EMPTY: Filters = { from: "", to: "", actor: "", action: "", entityType: "", entityId: "" };

/**
 * Console module: <b>Audit log</b>.
 *
 * The record has been written since the first release; this is the first thing that reads it back,
 * which is the difference between an audit trail and a claim about one.
 */
export function AuditLogPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);

  const actors = useAsync(() => principals.principals_List());

  const result = useAsync(
    () => auditClient.audit_List(
      applied.from ? new Date(applied.from).toISOString() : undefined,
      applied.to ? new Date(applied.to).toISOString() : undefined,
      applied.actor || undefined,
      applied.action || undefined,
      applied.entityType || undefined,
      applied.entityId || undefined,
      PAGE_SIZE,
      offset,
    ),
    [applied, offset],
  );

  const page: AuditEventView[] = result.data?.events ?? [];
  const hasMore = !!result.data?.hasMore;
  const loading = result.loading;
  const error = result.error;

  function apply() {
    setOffset(0);
    setApplied(filters);
  }

  function clear() {
    setFilters(EMPTY);
    setApplied(EMPTY);
    setOffset(0);
  }

  const actorName = (id?: string) =>
    (actors.data ?? []).find((a: PrincipalSummary) => a.id === id)?.name;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Audit log</h1>
          <p className="sub">
            Every administrative action, with who did it and when. Entries are never changed or
            deleted.
          </p>
        </div>
      </div>

      {error && <Alert kind="error">{error}</Alert>}

      <Panel title="Filters">
        <div className="row">
          <Field label="From">
            <input
              type="datetime-local"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            />
          </Field>
          <Field label="To">
            <input
              type="datetime-local"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            />
          </Field>
          <Field label="Actor">
            <select
              value={filters.actor}
              onChange={(e) => setFilters({ ...filters, actor: e.target.value })}
            >
              <option value="">anyone</option>
              {(actors.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>{a.name}{a.email ? ` — ${a.email}` : ""}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="row" style={{ marginTop: 12 }}>
          {/* The datalist sits outside the Field so the Field has one child to label. It is
              invisible either way, and a caption that points at nothing helps nobody. */}
          <datalist id="audit-actions">
            {[...new Set(page.map((e) => e.action).filter(Boolean))].map((a) => (
              <option key={a} value={a} />
            ))}
          </datalist>
          <Field label="Action (matches by prefix)">
            <input
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              placeholder="party"
              list="audit-actions"
            />
          </Field>
          <Field label="Entity type">
            <select
              value={filters.entityType}
              onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
            >
              <option value="">any</option>
              {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Entity id or key">
            <input
              value={filters.entityId}
              onChange={(e) => setFilters({ ...filters, entityId: e.target.value })}
              placeholder="p_118"
            />
          </Field>
          <button onClick={apply}>Search</button>
          <button className="ghost" onClick={clear}>Clear</button>
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
          Action matches a prefix: <span className="mono">party</span> finds{" "}
          <span className="mono">party.created</span>, <span className="mono">party.updated</span>{" "}
          and <span className="mono">party.node_changed</span>. Entity id is the key the action
          names — a party key, stream key or principal id.
        </p>
      </Panel>

      <Panel
        title="History"
        actions={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span className="muted" style={{ fontSize: 12 }}>
              {offset + 1}–{offset + page.length}
            </span>
            <button
              className="ghost small"
              disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Newer
            </button>
            <button
              className="ghost small"
              disabled={!hasMore || loading}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Older
            </button>
          </div>
        }
      >
        {loading ? (
          <Empty>Loading…</Empty>
        ) : page.length === 0 ? (
          <Empty>
            {applied === EMPTY
              ? "Nothing recorded yet."
              : "No events match those filters. Widen the date range or clear the action filter."}
          </Empty>
        ) : (
          <table>
            <thead>
              <tr><th>When</th><th>Who</th><th>Action</th><th>Entity</th><th></th></tr>
            </thead>
            <tbody>
              {page.map((e) => (
                <Fragment key={e.id}>
                  <tr>
                    <td className="muted" style={{ whiteSpace: "nowrap" }}>{when(e.occurredAt)}</td>
                    <td>
                      {actorName(e.principalId) ?? e.principalName}
                      {e.ipAddress && <div className="muted mono">{e.ipAddress}</div>}
                    </td>
                    <td className="mono">{e.action}</td>
                    <td>
                      <div>{e.entityType}</div>
                      {e.entityId && <div className="muted mono">{e.entityId}</div>}
                    </td>
                    <td>
                      {e.data && (
                        <button
                          className="ghost small"
                          onClick={() => setExpanded(expanded === e.id ? null : e.id!)}
                        >
                          {expanded === e.id ? "Hide" : "Detail"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === e.id && e.data && (
                    <tr>
                      <td colSpan={5}>
                        <div className="mono-block">{prettify(e.data)}</div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}

function prettify(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}
